import { Menu, MenuItem } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LIMIT_PER_PAGE } from "../../Common/Constants";
import { dateFormat } from "../../Common/DateAndTimeFormat";
import DateSelector from "../../Common/DateSelector";
import { debounce } from "../../Common/debounce";
import EditableTable from "../../Common/EditableTable";
import { Loader } from "../../Common/Loader";
import { Pagination } from "../../Common/Pagination";
import useDebounce from "../../Common/useDebounce";
import useDidMountEffect from "../../Common/useDidMountEffect";
import { CallInstantModal } from "../../Model/CallInstantModal";
import { ErrorModel } from "../../Model/ErrorModel";
import InstantEmailModal from "../../Model/InstantEmailModal";
import LowCreditModal from "../../Model/LowCreditModal";
import { SuccessModel } from "../../Model/SuccessModel";
import { getAssistantList } from "../../Redux/Slice/Assistant/AssistantsListSlice";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../Redux/Slice/Common/PaginationSlice";
import { setUnsavedChanges } from "../../Redux/Slice/Common/SelectedTabSlice";
import { getAllFormSubmissions } from "../../Redux/Slice/Form/GetAllFormSubmissionsSlice";
import { updateAssistantFormSubmissions } from "../../Redux/Slice/Form/UpdateAssistantFormSubmissionsSlice";
import { ScheduleCallPopup } from "../Contacts/ScheduleCallPopup";
import { ScheduleEmailPopup } from "../Contacts/ScheduleEmailPopup";

const AssistantEditableFormSubmissionsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const AssistantList = useSelector((state) => state.assistants.assistantsList);
  const { userPlanFeatures } = useSelector((s) => s?.userPlanDetails);
  const [isLoading, setIsLoading] = useState(false);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);
  const [filterData, setFilterData] = useState({
    name: "",
    assistant: "",
    startDate: null,
    endDate: null,
  });
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    visible: false,
    message: "",
  });
  const [schedulePopup, showSchedulePopup] = useState(false);
  const [scheduleEmailPopup, showScheduleEmailPopup] = useState(false);
  const [showInstantCallPopup, setShowInstantCallPopup] = useState({
    visible: false,
    data: null,
  });
  const [showInstantEmailPopup, setShowInstantEmailPopup] = useState({
    visible: false,
    data: null,
  });
  const [showLowCreditModal, setShowLowCreditModal] = useState(false);
  const [updatedSubmissionData, setUpdatedSubmissionData] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const searchQuery = useDebounce(filterData?.name, 500);
  const { allFormSubmissionsData, loading } = useSelector(
    (state) => state.allFormSubmissions
  );

  const currentFiltersCount = useMemo(() => {
    let count = 0;
    if (filterData?.startDate) {
      count++;
    }
    if (filterData?.endDate) {
      count++;
    }
    return count;
  }, [filterData]);

  const getFormSubmissions = async (page = 1, limit) => {
    setIsLoading(true);
    try {
      const result = await dispatch(
        getAllFormSubmissions({
          page: page,
          keyword: filterData?.name,
          from: filterData?.startDate
            ? moment(filterData?.startDate, "DD/MM/YYYY").utc().format()
            : null,
          to: filterData?.endDate
            ? moment(filterData?.endDate, "DD/MM/YYYY").utc().format()
            : null,
          assistantId: filterData?.assistant,
          limit,
        })
      );
      await dispatch(setPage(result?.payload?.pagination?.currentPage));
      await dispatch(setTotalItems(result?.payload?.pagination?.totalItems));
      await dispatch(setTotalPages(result?.payload?.pagination?.totalPages));
      dispatch(setUnsavedChanges(false));
      setHasChanged(false);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeSubmissionData = (data) => {
    setUpdatedSubmissionData(data);
    dispatch(setUnsavedChanges(true));
    setHasChanged(true);
  };

  const setPageList = (list) => {
    if (list && list?.data && Array.isArray(list?.data) && list?.data.length) {
      return list?.data.map((item) => {
        return {
          ...item,
          shareDate: dateFormat(item?.createdAt),
        };
      });
    }
    return [];
  };

  const debouncedGetFormsList = useCallback(debounce(getFormSubmissions, 500), [
    searchQuery,
    filterData.endDate,
    filterData.startDate,
    filterData.assistant,
  ]);

  useDidMountEffect(() => {
    if (filterData.assistant) {
      debouncedGetFormsList(1, localItemsPerPage);
    }
  }, [
    searchQuery,
    debouncedGetFormsList,
    filterData.endDate,
    filterData.startDate,
    filterData.assistant,
  ]);

  const onViewMore = (row) => {
    navigate("/assistant/form-submission/" + row?.id, {
      state: {
        name: row?.assistantName,
      },
    });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onCallInstant = (rowData, isMultiple) => {
    setShowInstantCallPopup({
      visible: true,
      data: rowData,
      isMultiple: isMultiple ?? false,
    });
    // navigate("/call/" + rowData.id);
  };
  const onEmailInstant = (rowData, isMultiple) => {
    setShowInstantEmailPopup({
      visible: true,
      data: rowData,
      isMultiple: isMultiple ?? false,
    });
  };

  const actionMenus = [];

  if (userPlanFeatures?.OUT_BOUND_CALL) {
    actionMenus.push(
      {
        title: "Instant Call",
        type: "INSTANT_CALL",
        onClick: (rowData) => {
          onCallInstant(rowData, true);
        },
      },
      {
        title: "Schedule Call",
        type: "SCHEDULE_CALL",
        onClick: (rowData) => {
          showSchedulePopup(true);
        },
      }
    );
  }

  if (userPlanFeatures?.WEB_CALLS) {
    actionMenus.push(
      {
        title: "Instant Email",
        type: "INSTANT_EMAIL",
        onClick: (rowData) => {
          onEmailInstant(rowData);
        },
      },
      {
        title: "Schedule Email",
        type: "SCHEDULE_EMAIL",
        onClick: (rowData) => {
          showScheduleEmailPopup(true);
        },
      }
    );
  }

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const updateFormData = async () => {
    if (!updatedSubmissionData) return;
    const payload = {
      ...updatedSubmissionData,
      assistantId: filterData.assistant,
    };
    setIsLoading(true);
    const result = await dispatch(updateAssistantFormSubmissions(payload));
    setIsLoading(false);
    if (
      updateAssistantFormSubmissions.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Details Updated Successfully!", () =>
        getFormSubmissions(1, localItemsPerPage)
      );
    } else {
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const currentSelectedRows = useMemo(() => {
    if (!Array.isArray(updatedSubmissionData?.data)) {
      return [];
    }
    const list = updatedSubmissionData?.data.filter(
      (row) => row?.isRowSelected
    );

    return list;
  }, [updatedSubmissionData]);

  const FetchAssistantsList = async () => {
    const result = await dispatch(
      getAssistantList({ text: "", isDropdown: true })
    );
    if (
      getAssistantList.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      const firstAssistant =
        result?.payload?.data &&
        Array.isArray(result?.payload?.data) &&
        result?.payload?.data?.length
          ? result?.payload?.data[0]
          : "";
      if (firstAssistant) {
        setFilterData((prev) => ({
          ...prev,
          assistant: firstAssistant?.id,
        }));
      }
    }
  };

  const closeInstantCallPopup = (value = "default", error = null) => {
    setShowInstantCallPopup({
      visible: false,
      data: null,
    });
    if (value === "success") {
      showSuccessMessage("Call Scheduled Successfully!");
    } else if (value === "error") {
      setIsLoading(false);
      const apiErrorMessage =
        error && typeof error?.message == "string" ? error?.message : null;
      const apiErrorCode = error && error?.code ? error?.code : null;
      if (apiErrorCode == 1030) {
        setShowLowCreditModal(true);
      } else {
        showErrorMessage(apiErrorMessage ?? "Something went wrong.");
      }
    }
  };

  const closeInstantEmailPopup = (value = "default", error = null) => {
    setShowInstantEmailPopup({
      visible: false,
      data: null,
    });
    if (value === "success") {
      showSuccessMessage("Email Sent successfully!");
    } else if (value === "error") {
      setIsLoading(false);
      const apiErrorMessage =
        error && typeof error?.message == "string" ? error?.message : null;
      const apiErrorCode = error && error?.code ? error?.code : null;
      if (apiErrorCode == 1030) {
        setShowLowCreditModal(true);
      } else {
        showErrorMessage(apiErrorMessage ?? "Something went wrong.");
      }
    }
  };

  const closePopup = (value = "default", error = null) => {
    showSchedulePopup(false);
    if (value === "success") {
      showSuccessMessage("Call Scheduled Successfully!");
    } else if (value === "error") {
      setIsLoading(false);
      const apiErrorMessage =
        error && typeof error?.message == "string" && error?.message
          ? error?.message
          : null;
      const apiErrorCode = error && error?.code ? error?.code : null;
      if (apiErrorCode == 1030) {
        setShowLowCreditModal(true);
      } else {
        showErrorMessage(apiErrorMessage ?? "Something went wrong.");
      }
    }
  };

  const closeScheduleEmailPopup = (value = "default", error = null) => {
    showScheduleEmailPopup(false);
    if (value === "success") {
      showSuccessMessage("Email Scheduled Successfully!");
    } else if (value === "error") {
      setIsLoading(false);
      const apiErrorMessage =
        error && typeof error?.message == "string" && error?.message
          ? error?.message
          : null;
      const apiErrorCode = error && error?.code ? error?.code : null;
      if (apiErrorCode == 1030) {
        setShowLowCreditModal(true);
      } else {
        showErrorMessage(apiErrorMessage ?? "Something went wrong.");
      }
    }
  };

  useEffect(() => {
    FetchAssistantsList();
  }, []);

  useEffect(() => {
    setUpdatedSubmissionData(null);
  }, [JSON.stringify(allFormSubmissionsData)]);

  return (
    <>
      {isLoading || loading ? <Loader /> : null}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {showLowCreditModal ? (
        <LowCreditModal onClose={() => setShowLowCreditModal(false)} />
      ) : null}
      {showInstantCallPopup.visible ? (
        <CallInstantModal
          closePopup={closeInstantCallPopup}
          currentContact={showInstantCallPopup.data}
          isMultiple
          contactsList={() => currentSelectedRows}
          isForForm
        />
      ) : null}
      {showInstantEmailPopup.visible ? (
        <InstantEmailModal
          closePopup={closeInstantEmailPopup}
          currentContact={showInstantEmailPopup.data}
          isMultiple
          contactsList={() => currentSelectedRows}
          isForForm
        />
      ) : null}
      {schedulePopup ? (
        <ScheduleCallPopup
          closePopup={closePopup}
          contactsList={() => currentSelectedRows}
          isForForm
        />
      ) : (
        ""
      )}
      {scheduleEmailPopup ? (
        <ScheduleEmailPopup
          closePopup={closeScheduleEmailPopup}
          contactsList={() => currentSelectedRows}
          isForForm
        />
      ) : (
        ""
      )}
      <div className="page__body">
        <div className="tableWrap">
          <div className="tableWrap__head tableWrap__head--wrap">
            <div
              className="formRow m_0 formRow--flexEnd"
              style={{ width: "auto" }}
            >
              <div className="formCol formCol--flex">
                <div className="formCol flexField">
                  <input
                    className="form__input form__input--search"
                    type="text"
                    placeholder="Search a Name"
                    value={filterData?.name}
                    onChange={(e) =>
                      setFilterData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <select
                    className="form__select form__select--sm"
                    id="Chatbot"
                    name="chatbotId"
                    value={filterData?.assistant}
                    onChange={({ target: { value } }) =>
                      setFilterData((prev) => ({ ...prev, assistant: value }))
                    }
                  >
                    <option selected value="" disabled>
                      Select Assistant
                    </option>
                    {Array.isArray(AssistantList?.data)
                      ? AssistantList?.data?.map((assistant) => (
                          <option value={assistant?.id}>
                            {assistant?.name}
                          </option>
                        ))
                      : ""}
                  </select>
                </div>
              </div>
            </div>
            <div className="formCol formCol--flex">
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <DateSelector
                      placeholder="Start Date"
                      value={filterData.startDate}
                      onChangeValue={(value) =>
                        setFilterData((prev) => ({
                          ...prev,
                          startDate: dateFormat(value),
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <DateSelector
                      placeholder="End Date"
                      value={filterData.endDate}
                      onChangeValue={(value) =>
                        setFilterData((prev) => ({
                          ...prev,
                          endDate: dateFormat(value),
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="btn__group">
                  <button
                    className="btn btn__primary"
                    disabled={
                      !updatedSubmissionData ||
                      !Array.isArray(updatedSubmissionData?.columns) ||
                      !updatedSubmissionData?.columns?.length ||
                      !hasChanged
                    }
                    onClick={() => updateFormData()}
                  >
                    Save
                  </button>
                  {currentSelectedRows.length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="btn btn__primary btn__outlined btn__circle"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        <svg
                          width={6}
                          height={24}
                          viewBox="0 0 6 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx={3} cy={3} r={3} fill="#8b58f1" />
                          <circle cx={3} cy={12} r={3} fill="#8b58f1" />
                          <circle cx={3} cy={21} r={3} fill="#8b58f1" />
                        </svg>
                      </button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                      >
                        {Array.isArray(actionMenus)
                          ? actionMenus?.map((item, index) => (
                              <MenuItem
                                key={`${item?.title}-${index}`}
                                sx={{
                                  padding: ".70rem 1rem",
                                  fontSize: ".75rem",
                                  fontWeight: "400",
                                  width: 160,
                                }}
                                onClick={() => {
                                  handleClose();
                                  item.onClick();
                                }}
                              >
                                {item?.title}
                              </MenuItem>
                            ))
                          : null}
                      </Menu>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {currentFiltersCount > 0 ? (
            <div className="tableWrap__record">
              <h3 className="title">
                Showing Results ({setPageList(allFormSubmissionsData)?.length}{" "}
                of {allFormSubmissionsData?.pagination?.totalItems ?? 0} Forms)
              </h3>
              <button
                className="btn btn__white"
                onClick={() => {
                  setFilterData({
                    name: "",
                    assistant: filterData.assistant,
                    startDate: null,
                    endDate: null,
                  });
                }}
              >
                Clear Filter
              </button>
            </div>
          ) : null}
          {/* <Table
            data={setPageList(allFormSubmissionsData)}
            columns={FORM_COLOUMS}
            onViewTransactions={onViewMore}
            actionText="View More"
            hasAction="true"
          /> */}
          {Array.isArray(allFormSubmissionsData?.data?.columns) &&
          allFormSubmissionsData?.data?.columns?.length ? (
            <div style={{ overflow: "auto", display: "flex" }}>
              <div
                style={{
                  flex: "1 1 auto",
                }}
              >
                <EditableTable
                  data={allFormSubmissionsData?.data}
                  isMultiSelect
                  onSelectAll={() => {}}
                  onChangeData={onChangeSubmissionData}
                />
              </div>
            </div>
          ) : (
            <div className="no-data">No Submissions Found!</div>
          )}
        </div>
      </div>
      <Pagination
        apiMethod={getFormSubmissions}
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </>
  );
};

export default AssistantEditableFormSubmissionsList;
